// WorkspaceProvider — Ring App Store tenant resolution.
//
// On mount, calls `GET /api/session` (BFF) and exposes a
// `WorkspaceDescriptor` to consumers. 401 / 404 leave workspace=null so
// callers can route the user to the pre-session tenant picker. Mid-session
// disconnects (force-unlink, Ring webhook revoke) are surfaced via
// `markDisconnected()`, which the WorkspaceDisconnectedModal listens for.
//
// This is intentionally separate from upstream's SessionProvider: that
// owns `/api/session/me` (alias + grants), this owns `/api/session`
// (tenant-shaped descriptor).

import {
  useCallback,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { ApiError, api } from "@/api";
import type { WorkspaceDescriptor } from "@/types/tenant";
import { WorkspaceContext, type WorkspaceContextValue } from "./context";

interface WorkspaceState {
  workspace: WorkspaceDescriptor | null;
  loading: boolean;
  error: string | null;
  disconnected: boolean;
}

const INITIAL: WorkspaceState = {
  workspace: null,
  loading: true,
  error: null,
  disconnected: false,
};

export function WorkspaceProvider({ children }: { children: ReactNode }): JSX.Element {
  const [state, setState] = useState<WorkspaceState>(INITIAL);

  const refresh = useCallback(async (): Promise<void> => {
    setState((s) => ({ ...s, loading: true, error: null }));
    try {
      // skipActingRole + skipAuthRedirect: this endpoint is the pre-role
      // bootstrap; the upstream client wrapper would otherwise route a 401
      // to /signin AND require X-Acting-Role, neither of which apply.
      const workspace = await api.get<WorkspaceDescriptor>("/api/session", {
        skipActingRole: true,
        skipAuthRedirect: true,
      });
      setState({ workspace, loading: false, error: null, disconnected: false });
    } catch (err) {
      if (err instanceof ApiError && (err.status === 401 || err.status === 404)) {
        // No active workspace; consumers route to /tenant-picker (multi-
        // tenant) or /signin (no session). We don't navigate from here so
        // the provider stays composable.
        setState({ workspace: null, loading: false, error: null, disconnected: false });
        return;
      }
      const msg = err instanceof Error ? err.message : "Unknown error";
      setState({ workspace: null, loading: false, error: msg, disconnected: false });
    }
  }, []);

  const markDisconnected = useCallback((): void => {
    setState((s) => ({ ...s, disconnected: true }));
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const value = useMemo<WorkspaceContextValue>(
    () => ({ ...state, refresh, markDisconnected }),
    [state, refresh, markDisconnected],
  );

  return <WorkspaceContext.Provider value={value}>{children}</WorkspaceContext.Provider>;
}
